import { 
  useContext, 
  useEffect, 
  useState, 
  useCallback,
  useRef
} from "react";

import { 
  Text, 
  Button, 
  TextField, 
  Icon 
} from "@aidron/aidron-ds";

import { TenantContext } from "../../../templates/context/TenantContext";
import { i18n } from "../../../translate/i18n";

import {
  OuterContainer, 
  InnerContainer, 
  StyledTextField, 
  SearchButton, 
  InputtedWordWrapper, 
  IconWrapper
} from "./HierarchyFilter.style";

import { 
  div,
  CustomText
} from "../../StyledComponents/styleComponents.index";

import PropTypes from 'prop-types';
import { textFormatter } from "../../../utils/formatter/formatter";

const { t } = i18n;

function HierarchyFilterBox({ 
  productIds, 
  allProducts,
  level, 
  selectedProductIds, 
  selectedLevels, 
  hierarchy,
  setSelectedLevels 
}) {

  const scrollableDivRef = useRef(null);

  const hierarchyName = hierarchy.labels[level];

  const products = allProducts.filter(product => productIds.includes(product.productId)) || [];

  const optionNames = [...new Set(products.map(product => product[level]))];

  const options = optionNames.map(option => {
    const productsWithThatLevel = products.filter(product => product[level] === option);
    const ids = productsWithThatLevel.map(product => product.productId);
    return {
      name: option,
      ids: ids,
    };
  });

  const alphabeticallyOrderedOptions = options.slice().sort((a, b) => a?.name?.localeCompare(b.name));

  const isOptionPossible = (optionName) => {
    const selectedProductsIdsNames = allProducts.filter(product => selectedProductIds.includes(product.productId));
    return selectedProductsIdsNames.some(product => product[level] === optionName);
  }

  const possibleOptions = alphabeticallyOrderedOptions.filter(option => isOptionPossible(option.name));
  const disabledOptions = alphabeticallyOrderedOptions.filter(option => !isOptionPossible(option.name));

  const orderedOptions = possibleOptions.concat(disabledOptions);

  const isOptionSelected = (optionName, level) => selectedLevels[level]?.includes(optionName);

  const onClick = (option) => {
    let newSelectedLevel = selectedLevels[level] ? [...selectedLevels[level]] : [];

    if (isOptionSelected(option.name)) {
      const indexOfSelectedHierarchy = newSelectedLevel.indexOf(option.name);
      newSelectedLevel.splice(indexOfSelectedHierarchy, 1);
    } else newSelectedLevel.push(option.name);

    setSelectedLevels({
      ...selectedLevels,
      [level]: newSelectedLevel,
    });

    scrollToTop();
  };

  const scrollToTop = () => scrollableDivRef.current.scrollTo({ top: 0 });

  return (
    <div className="d-flex flex-column justify-content-between width-fit-content border-radius-8px box-shadow-black-2-0-11-0 mb-3 me-2 px-3 pb-2">
      <Text weight="bold">{hierarchyName}</Text>
      <div className="d-flex justify-content-center pb-1">
        <div className="width-180px border-top-light-grey-solid-1px me-2"></div>
      </div>
      <div className="height-175px width-200px overflow-scroll pe-2" ref={scrollableDivRef}>
        {orderedOptions.map((option) => (
          <Button
            label={textFormatter(option.name)}
            onClick={() => onClick(option)}
            type={isOptionSelected(option.name) ? "outlined" : "ghost"}
            className="box-shadow-unset box-shadow-unset-on-button text-align-start-on-button text-align-start"
            disabled={!isOptionPossible(option.name)}
            key={option.name}
          />
        ))}
      </div>
    </div>
  );
};

function SearchInput({ inputtedExpressions, setInputtedExpressions, typedExpression, setTypedExpression }) {
  
  function removeExpression(index) {
    const newExpressions = [...inputtedExpressions];
    newExpressions.splice(index, 1);
    setInputtedExpressions(newExpressions);
    setTypedExpression("");
  };

  function search() {
    setInputtedExpressions([typedExpression]);
  };

  const cutExpression = useCallback((text) => {
    if (text.length <= 21) return text;
    return text.substring(0, 21) + "...";
  }, []);

  useEffect(() => {
    const keyDownHandler = (e) => {
      if (e.key === "Enter" && typedExpression.trim()) search();
    };
    
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [typedExpression]);

  return (
    <OuterContainer>
      <Text size="sm" weight="bold">
        {t("searchByHierarchy")}
      </Text>

      <InnerContainer>
        <StyledTextField>
          <TextField
           onChange={setTypedExpression} 
           label={`${t("type")}...`} 
           placeholder="" 
           value={typedExpression} />
        </StyledTextField>

        <SearchButton
          label=""
          onClick={() => {
            if (typedExpression) search();
          }}
          icon="search"
          type="outlined"/>
      </InnerContainer>

      {inputtedExpressions.map((expression, index) => (
        <InputtedWordWrapper key={expression}>
          <CustomText fontSize={['0.3rem', '0.6rem', '1rem']} color="orange" weight="bold">
            {cutExpression(expression)}
          </CustomText>

          <IconWrapper onClick={() => removeExpression(index)}>
            <Icon icon="x" size={16} color="color-primary-medium" />
          </IconWrapper>
        </InputtedWordWrapper>
      ))}
    </OuterContainer>
  );
};

export function HierarchyFilters({ actions, ctx, uiState }) {

  const { hierarchy } = useContext(TenantContext);

  const { 
    setSelectedProductIds,
    setAppliedFiltersCount,
    setSelectedHierarchyFilters = () => {}
  } = actions || {};

  const { productIds, selectedProductIds, allProducts } = ctx || {};

  const { showFilters } = uiState || {};
  
  const[inputtedExpressions, setInputtedExpressions] = useState([]);
  const[selectedLevels, setSelectedLevels] = useState({});
  const [typedExpression, setTypedExpression] = useState("");

  const isAnyHierarchySelected = () => {
    const selectedHierarchies = Object.values(selectedLevels);
    return selectedHierarchies.some(selected => selected.length);
  }

  const filterProductsByHierarchy = () => {
    let filteredProducts = productIds.map(productId => allProducts.find(product => product.productId === productId));
    filteredProducts = filteredProducts.filter(product => product);
    for (const level in selectedLevels) {
      if (selectedLevels[level].length) {
        filteredProducts = filteredProducts.filter(product => {
          return selectedLevels[level].includes(product[level]);
        });
      }
    }
    return filteredProducts;
  }

  const getProductIdsByExpression = () => {
    const expression = inputtedExpressions[0].toUpperCase();
    const filteredByExpression = allProducts.filter(product => {
      return hierarchy.levels.some(level => product[level].toUpperCase().includes(expression));
    });
    return filteredByExpression.map(product => product.productId);
  }

  const appliedFiltersNumber = () => {
    let count = 0;
    if (isAnyHierarchySelected()) count += 1;
    if (inputtedExpressions.length) count += 1;
    return count;
  }

  const isAnyFilterApplied = () => appliedFiltersNumber() !== 0;

  const resetFilters = () => {
    setTypedExpression("");
    setInputtedExpressions([]);
    setSelectedLevels({});
  }

  useEffect(() => {
    let filteredProductIds = productIds;

    if (isAnyHierarchySelected()) filteredProductIds = filterProductsByHierarchy().map(product => product.productId);

    if (inputtedExpressions.length) {
      const productsIdsByExpression = getProductIdsByExpression();
      filteredProductIds = filteredProductIds.filter(productId => productsIdsByExpression.includes(productId));
    }

    setSelectedProductIds(filteredProductIds);
    setAppliedFiltersCount(appliedFiltersNumber());

    setSelectedHierarchyFilters({
      selectedLevels: selectedLevels,
      inputtedExpressions: inputtedExpressions
    });
  }, [selectedLevels, inputtedExpressions]);

  useEffect(() => {
    setSelectedProductIds(productIds);
    setSelectedHierarchyFilters({
      selectedLevels: {},
      inputtedExpressions: []
    });
  }, [productIds]);  

  return (
    <div style={{
      display: showFilters ? "flex" : "none",
      flexDirection: "column",
      margin: "10px 0",
      padding: "10px 30px",
      backgroundColor: "#fcfcfc",
      border: "0.0625rem solid rgba(17, 24, 39, 0.125)",
      boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.04)",
      borderRadius: "4px",
      wordWrap: "break-word",      
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <SearchInput
          inputtedExpressions={inputtedExpressions}
          setInputtedExpressions={setInputtedExpressions}
          typedExpression={typedExpression}
          setTypedExpression={setTypedExpression}
        />
        <Button
          label={t("clearFilters")}
          type="outlined"
          icon="x"
          position="left"
          onClick={resetFilters}
          disabled={!isAnyFilterApplied()}
        />
      </div>

      <Text size="sm" weight="bold" className="mb-2">
        {t("filterByHierarchy")}
      </Text>

      <div className="d-flex" style={{ overflowX: 'scroll' }}>
        {hierarchy.levels.map((level) => (
          <HierarchyFilterBox
            productIds={productIds}
            allProducts={allProducts}
            level={level}
            selectedProductIds={selectedProductIds}
            selectedLevels={selectedLevels}
            setSelectedLevels={setSelectedLevels}
            hierarchy={hierarchy}
            key={level}
          />
        ))}
      </div>
    </div>
  );
};

HierarchyFilterBox.propTypes = {
  productIds: PropTypes.arrayOf(
    PropTypes.number
  ),
  level: PropTypes.string,
  selectedProductIds: PropTypes.arrayOf(
    PropTypes.number
  ),
  selectedLevels: PropTypes.shape(),
  setSelectedLevels: PropTypes.func
};

SearchInput.propTypes = {
  inputtedExpressions: PropTypes.arrayOf(
    PropTypes.string
  ),
  setInputtedExpressions: PropTypes.func,
  typedExpression: PropTypes.string,
  setTypedExpression: PropTypes.func
};

HierarchyFilters.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};